import * as React from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';
import {
  hasReloadedRecently,
  isChunkLoadError,
  isReloading,
  tryReloadOnce,
} from '../../utils/chunk-error';

const RELOAD_SAFETY_TIMEOUT = 10_000;

// Sentry invoca o FallbackRender como função simples durante o render do
// próprio ErrorBoundary (class component), então hooks não podem ser usados
// no nível do FallbackRender. Renderizamos um componente interno de verdade
// pra poder usar useEffect com segurança.
const SentryFallback: Sentry.FallbackRender = (props) => (
  <SentryFallbackContent {...props} />
);

type SentryFallbackContentProps = {
  error: unknown;
  resetError: () => void;
};

const SentryFallbackContent = ({
  error,
  resetError,
}: SentryFallbackContentProps) => {
  const isChunk = error instanceof Error && isChunkLoadError(error);

  const [reloadTimedOut, setReloadTimedOut] = useState(false);

  // Mesma estratégia do ErrorPage (Camada C). isReloading() cobre o caso da
  // Camada B ter agendado o reload antes; o segundo termo cobre o boundary
  // capturando o chunk direto. reloadTimedOut é a saída anti-spinner-infinito.
  const willAutoReload =
    !reloadTimedOut &&
    (isReloading() || (isChunk && !hasReloadedRecently()));

  useLayoutEffect(() => {
    if (willAutoReload) tryReloadOnce('sentry-error-boundary');
  }, [willAutoReload]);

  useEffect(() => {
    if (!willAutoReload) return;
    const id = setTimeout(() => setReloadTimedOut(true), RELOAD_SAFETY_TIMEOUT);
    return () => clearTimeout(id);
  }, [willAutoReload]);

  // Reload a caminho: spinner neutro em vez do "Oops!" em corpo 4rem sobre
  // fundo cheio — o usuário lê como carregamento, não como crash.
  if (willAutoReload) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  const description = isChunk
    ? 'Sua versão do app está desatualizada. Recarregue a página para continuar.'
    : 'Algo inesperado aconteceu. Nossa equipe já foi notificada.';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#1a1a2e',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        textAlign: 'center',
        padding: '2rem',
      }}
    >
      <h1
        style={{
          fontSize: '4rem',
          fontWeight: 600,
          color: '#ffffff',
          margin: '0 0 1rem',
        }}
      >
        Oops!
      </h1>
      <p
        style={{
          fontSize: '1.25rem',
          color: '#a0a0b8',
          margin: '0 0 2rem',
          maxWidth: '480px',
          lineHeight: 1.6,
        }}
      >
        {description}
      </p>
      {error instanceof Error && !isChunk && (
        <pre
          style={{
            fontSize: '0.8rem',
            color: '#e74c3c',
            backgroundColor: '#1e1e30',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            maxWidth: '600px',
            overflow: 'auto',
            marginBottom: '2rem',
          }}
        >
          {error.message}
        </pre>
      )}
      <div style={{ display: 'flex', gap: '1rem' }}>
        <button
          onClick={() => {
            resetError();
            window.location.href = '/orders';
          }}
          style={{
            backgroundColor: '#ffffff',
            color: '#1a1a2e',
            border: 'none',
            borderRadius: '8px',
            padding: '0.875rem 2rem',
            fontSize: '1rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = '0.85')}
          onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
        >
          Voltar ao início
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: 'transparent',
            color: '#ffffff',
            border: '1px solid #ffffff40',
            borderRadius: '8px',
            padding: '0.875rem 2rem',
            fontSize: '1rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = '0.85')}
          onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
        >
          Recarregar página
        </button>
      </div>
    </div>
  );
};

export default SentryFallback;
